(() => {
  if (window.__driftHeaderViewportController) {
    window.__driftHeaderViewportController.update?.();
    return;
  }

  const ROOT = document.documentElement;
  const HERO_SELECTOR =
    '.shopify-section.drift-hero-section, .drift-hero-section';

  /*
    Shopify/Horizon puts the announcement and main navigation sections
    in the header section group. We measure the VISIBLE UNION of those
    elements rather than hard-coding either bar's height.

    Page top:
      announcement + nav are counted.

    After scrolling:
      announcement leaves the viewport and only sticky nav is counted.
  */
  const HEADER_SELECTOR =
    '.shopify-section-group-header-group';

  let frame = 0;
  let resizeObserver = null;
  let observedHeaders = [];

  function elementIsRenderable(element) {
    if (!element) return false;

    const style = window.getComputedStyle(element);

    return (
      style.display !== 'none' &&
      style.visibility !== 'hidden' &&
      parseFloat(style.opacity || '1') !== 0
    );
  }

  function visibleIntervals() {
    const viewportHeight =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0;

    return Array.from(
      document.querySelectorAll(HEADER_SELECTOR)
    )
      .filter(elementIsRenderable)
      .map((element) => {
        const rect = element.getBoundingClientRect();

        const start = Math.max(0, rect.top);
        const end = Math.min(viewportHeight, rect.bottom);

        return {
          start,
          end
        };
      })
      .filter((interval) => interval.end > interval.start)
      .sort((a, b) => a.start - b.start);
  }

  function unionHeight(intervals) {
    if (!intervals.length) return 0;

    let total = 0;
    let start = intervals[0].start;
    let end = intervals[0].end;

    for (let index = 1; index < intervals.length; index += 1) {
      const interval = intervals[index];

      if (interval.start <= end) {
        end = Math.max(end, interval.end);
      } else {
        total += end - start;
        start = interval.start;
        end = interval.end;
      }
    }

    total += end - start;

    return total;
  }

  function measureHeaderOffset() {
    /*
      Do nothing on pages where the Drift Hero is not present.
      This keeps this utility isolated even if the asset is accidentally
      loaded somewhere else later.
    */
    if (!document.querySelector(HERO_SELECTOR)) {
      ROOT.style.removeProperty('--drift-header-offset');
      return 0;
    }

    const offset =
      Math.max(
        0,
        Math.round(
          unionHeight(
            visibleIntervals()
          )
        )
      );

    ROOT.style.setProperty(
      '--drift-header-offset',
      `${offset}px`
    );

    return offset;
  }

  function update() {
    if (frame) return;

    frame = requestAnimationFrame(() => {
      frame = 0;
      measureHeaderOffset();
    });
  }

  function bindResizeObserver() {
    if (!('ResizeObserver' in window)) return;

    const headers =
      Array.from(
        document.querySelectorAll(HEADER_SELECTOR)
      );

    const sameHeaders =
      headers.length === observedHeaders.length &&
      headers.every(
        (header, index) =>
          header === observedHeaders[index]
      );

    if (sameHeaders) return;

    resizeObserver?.disconnect();

    resizeObserver =
      new ResizeObserver(update);

    headers.forEach((header) => {
      resizeObserver.observe(header);
    });

    observedHeaders = headers;
  }

  function refresh() {
    bindResizeObserver();
    update();
  }

  window.addEventListener(
    'scroll',
    update,
    { passive: true }
  );

  window.addEventListener(
    'resize',
    refresh
  );

  window.addEventListener(
    'pageshow',
    refresh
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (!document.hidden) refresh();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    refresh
  );

  document.addEventListener(
    'shopify:section:select',
    refresh
  );

  /*
    Small delayed checks catch late header/announcement initialization
    without introducing a permanent MutationObserver.
  */
  window.setTimeout(refresh, 100);
  window.setTimeout(refresh, 500);
  window.setTimeout(refresh, 1200);

  window.__driftHeaderViewportController = {
    update: refresh,
    measure: measureHeaderOffset
  };

  refresh();
})();
